/** Chamados de suporte do gestor/treinador: lista (na Ajuda), novo chamado e um chamado. */
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, upload } from "../../api/client";
import type { SupportTicketDetail } from "../../api/types";
import { IconChat } from "../../components/icons";
import { AttachmentPicker, TicketBrowser, TicketView, useHelpInfo } from "../../components/SupportTickets";
import { Button, FormError, PageHead, TextArea, TextField, useToast } from "../../components/ui";
import { Link, navigate, useLocation } from "../../router/router";
import { t } from "../../i18n";

const SUBJECT_MAX = 140;
const BODY_MAX = 2000;

export function UserTicketsSection({ orgId }: { orgId: string }) {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#suporte") document.getElementById("suporte")?.scrollIntoView({ block: "start" });
  }, [hash]);
  return (
    <div className="panel-body help-chat" id="suporte">
      <div className="ticket-section-head">
        <h3 className="help-chat-title">
          <IconChat width={18} height={18} /> {" "}{t("Chamados para o suporte EAPace")}
        </h3>
        <Link to={`/org/${orgId}/ajuda/chamados/novo`} className="btn btn-primary btn-sm">
          {t("Novo chamado")}
        </Link>
      </div>
      <p className="muted">
        {t("Cada solicitação vira um chamado com número próprio (SUP-…) e histórico. Quem responde é a administração do EAPace, que não vê os dados esportivos da sua assessoria. Para falar com um atleta, use a conversa dele.")}
      </p>
      <TicketBrowser
        listPath={`/orgs/${orgId}/support/tickets`}
        hrefFor={(tp) => `/org/${orgId}/ajuda/chamados/${tp.id}`}
        emptyTitle={t("Nenhum chamado por aqui.")}
        emptyHint={t("Quando precisar de ajuda, abra um chamado: ele ganha um número e você acompanha a resposta.")}
      />
    </div>
  );
}

export function NewTicketPage({ orgId }: { orgId: string }) {
  const toast = useToast();
  const info = useHelpInfo();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const valid = subject.trim().length >= 3 && body.trim().length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("subject", subject.trim());
      form.append("body", body.trim());
      files.forEach((f) => form.append("files", f));
      const tp = await upload<SupportTicketDetail>(`/orgs/${orgId}/support/tickets`, form);
      toast(t("Chamado {code} aberto.", { code: tp.code }), "ok");
      navigate(`/org/${orgId}/ajuda/chamados/${tp.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "unknown", String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead title={t("Novo chamado")} subtitle={t("Descreva o que você precisa. O chamado recebe um número e você acompanha a resposta aqui.")} />
      <form className="panel panel-body ticket-form" onSubmit={submit} aria-label={t("Novo chamado")}>
        <TextField
          label={t("Assunto")}
          value={subject}
          maxLength={SUBJECT_MAX}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t("Ex.: Não consigo convidar um treinador")}
          required
          autoFocus
          hint={`${subject.length}/${SUBJECT_MAX}`}
        />
        <TextArea
          label={t("Descrição")}
          rows={6}
          value={body}
          maxLength={BODY_MAX}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("O que aconteceu, o que você esperava e, se houver, a mensagem de erro.")}
          required
          hint={t("Não envie dados de saúde ou pessoais de atletas. {n}/{max}", { n: body.length, max: BODY_MAX })}
        />
        <AttachmentPicker files={files} onChange={setFiles} info={info} disabled={busy} />
        <FormError error={error} />
        <div className="ticket-form-actions">
          <Link to={`/org/${orgId}/ajuda#suporte`} className="btn btn-secondary">
            {t("Cancelar")}
          </Link>
          <Button type="submit" variant="primary" busy={busy} disabled={!valid}>
            {t("Abrir chamado")}
          </Button>
        </div>
      </form>
    </>
  );
}

export function UserTicketPage({ orgId, ticketId }: { orgId: string; ticketId: string }) {
  return (
    <TicketView
      apiBase={`/orgs/${orgId}/support/tickets`}
      ticketId={ticketId}
      backTo={`/org/${orgId}/ajuda#suporte`}
      backLabel={t("Meus chamados")}
    />
  );
}
